'use client';

import { useEffect, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { Stdv1Stat } from '../observability/constants';
import { recordMetric } from '../observability/metrics';

import {
  EBAY_POPUP_POLL_INTERVAL_MS,
  EBAY_SSO_MESSAGE_TYPE,
  EBAY_SSO_STATE_SESSION_STORAGE_KEY,
} from './constants';
import { openEbayPopup } from './ebayPopup';

import { fetchEbayAuthorizationUrl } from '@/modules/login/api';
import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@/modules/storage';

export type EbayAuthError =
  'popupBlocked' | 'authorizeFailed' | 'stateMismatch' | 'noCode';

type EbayCallbackMessage = {
  type: typeof EBAY_SSO_MESSAGE_TYPE;
  code?: string;
  state?: string;
};

type Props = {
  onEbayAuthorized: (code: string) => Promise<void> | void;
  onError: (error: EbayAuthError) => void;
};

export function useEbayAuth({ onEbayAuthorized, onError }: Props) {
  const t = useTranslations('common');
  const [isInProgress, setIsInProgress] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  function startPopupFlow() {
    cleanupRef.current?.();

    const popup = openEbayPopup(t('EbaySSO.PopupTitle'));

    if (!popup) {
      return onError('popupBlocked');
    }

    const state = crypto.randomUUID();
    setSessionStorageItem(EBAY_SSO_STATE_SESSION_STORAGE_KEY, state);
    setIsInProgress(true);

    const controller = new AbortController();
    let pollTimer: number | undefined;

    function cleanup() {
      controller.abort();
      window.clearInterval(pollTimer);
      removeSessionStorageItem(EBAY_SSO_STATE_SESSION_STORAGE_KEY);
      popup?.close();
      cleanupRef.current = null;
    }

    // Clean up the setup if the popup is closed
    pollTimer = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        setIsInProgress(false);
        recordMetric({
          type: 'increment',
          stat: Stdv1Stat['ebay.auth.popup_closed_by_user_error'],
        });
      }
    }, EBAY_POPUP_POLL_INTERVAL_MS);

    async function handleMessage(event: MessageEvent<EbayCallbackMessage>) {
      if (
        event.origin !== window.location.origin ||
        event.source !== popup ||
        event.data?.type !== EBAY_SSO_MESSAGE_TYPE
      ) {
        return;
      }

      const storedState = getSessionStorageItem(
        EBAY_SSO_STATE_SESSION_STORAGE_KEY
      );
      cleanup();

      const { code, state: returnedState } = event.data;

      if (!code) {
        setIsInProgress(false);
        return onError('noCode');
      }

      // CSRF protection to ensure the request is coming from the same origin as the popup
      if (!returnedState || returnedState !== storedState) {
        setIsInProgress(false);
        recordMetric({
          type: 'increment',
          stat: Stdv1Stat['ebay.auth.state_mismatch_error'],
        });
        return onError('stateMismatch');
      }

      try {
        await onEbayAuthorized(code);
      } finally {
        setIsInProgress(false);
      }
    }

    window.addEventListener('message', handleMessage, {
      signal: controller.signal,
    });
    cleanupRef.current = cleanup;

    fetchEbayAuthorizationUrl(state)
      .then(({ data }) => {
        if (!popup.closed) {
          // Redirect to the eBay authorization URL
          popup.location.href = data.authorization_url;
        }
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        cleanup();
        setIsInProgress(false);
        onError('authorizeFailed');
        recordMetric({
          type: 'increment',
          stat: Stdv1Stat['ebay.auth.authorize_error'],
        });
      });
  }

  return { startPopupFlow, isInProgress };
}
